import React, { useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import clsx from 'clsx';
import { useUpdateLead } from 'src/services/api';

import { ThemeProvider } from 'src/theme';
import { FlexColumn } from 'src/components/flex';
import PageTitle from 'src/components/page-title/PageTitle';
import { JustAddedFactsProvider } from 'src/components/lead-sidebar/just-added-facts-context';
import Provider from 'src/pages/landlord/leads/messages/context/Provider';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import MessagesContainer from 'src/pages/landlord/leads/messages/components/messages-container';
import Header from 'src/pages/landlord/leads/messages/components/header';
import Input from 'src/pages/landlord/leads/messages/components/input';
import Hotkeys from 'src/pages/landlord/leads/messages/components/hotkeys/LeadMessagesHotkeys';
import LeadSidebar from 'src/components/lead-sidebar/LeadSidebar';
import SmartFieldsModal from 'src/pages/landlord/leads/messages/components/smart-fields-modal';
import SentryErrorBoundary from 'src/components/sentry-error-boundary';
import SnippetsModal from 'src/pages/landlord/leads/messages/components/snippets-modal';
import CreateOrUpdateSnippetModal from 'src/pages/landlord/snippets/components/create-or-update-snippet-modal';
import SmartNotifications from 'src/pages/landlord/leads/messages/components/smart-notifications';
import CannotReplyNotice from 'src/pages/landlord/leads/messages/components/notice-bars/CannotReplyNotice';
import OwnerAwayNotice from 'src/pages/landlord/leads/messages/components/notice-bars/OwnerAwayNotice';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import { useLandlordLayoutContext } from 'src/components/layout/landlord/LandlordLayoutProvider';

import styles from 'src/pages/landlord/leads/messages/LeadMessagesPage.module.css';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal';

function LeadMessagesPageContent() {
    const {
        showings,
        lead,
        property,
        messages,
        attachments,
        snippets,
        stages,
        interests,
        teamMembers,
        setReplyInput,
        sendMessage,
        isSnippetsModalOpen,
        setIsSnippetsModalOpen,
        navigateToPreviousConversation,
        navigateToNextConversation,
        loggedInUser,
        isOwnerAway,
        isAvailabilityEnabled,
    } = useLeadMessagesPageContext();

    const isOwnLead = loggedInUser.id === lead.owner;
    // Three mutually-exclusive states for a lead the user does not own:
    // 1. availability feature off  -> cannot reply, show the plain warning
    // 2. feature on, owner online  -> cannot reply, show the plain warning
    // 3. feature on, owner away    -> may cover the shift, show the "away" bar
    const isTeammateLead = !isOwnLead;
    const isOwnerAwayTeammate = isTeammateLead && isAvailabilityEnabled && isOwnerAway;
    const isOwnerOnlineTeammate = isTeammateLead && isAvailabilityEnabled && !isOwnerAway;
    const isTeammateLeadFeatureOff = isTeammateLead && !isAvailabilityEnabled;
    // The plain "does not belong to you" warning covers both no-reply cases.
    const showCannotReplyWarning = isTeammateLeadFeatureOff || isOwnerOnlineTeammate;
    const { mutate: updateLead, isLoading: isAssigningLead } = useUpdateLead(lead.uuid);
    const assignLeadToSelf = () => {
        updateLead({
            uuid: lead.uuid,
            name: lead.name,
            email: lead.email,
            phone_number: lead.phone_number,
            property_id: lead.property_id,
            owner: loggedInUser.id,
        });
    };

    const { isDrawerOpen } = useLandlordLayoutContext();

    const [isShowLeadDetailsModal, setIsShowLeadDetailsModal] = useState(false);
    const [isAddingSnippet, setIsAddingSnippet] = useState(false);
    const [isTourModalOpen, setIsTourModalOpen] = useState(false);

    const handleCloseShowingModal = () => {
        setIsTourModalOpen(false);
    };

    useEffect(() => {
        const handleKeyDown = debounce((event: KeyboardEvent) => {
            const isUserTyping = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName as string) ||
                (document.activeElement as HTMLElement)?.isContentEditable;
            if (isUserTyping) {
                return;
            }
            if (['ArrowUp', 'ArrowDown', 'T', 't'].includes(event.key)) {
                analyticsClient.track(AnalyticsEvent.SHORTCUT_KEY_USED, {
                    page: 'Lead messages',
                    key: event.key,
                });
            }

            switch (event.key) {
                case 'ArrowUp':
                    navigateToPreviousConversation();
                    break;
                case 'ArrowDown':
                    navigateToNextConversation();
                    break;
                case 't':
                case 'T':
                    setIsTourModalOpen(true);
                    break;
                case 's':
                case 'S':
                    setIsSnippetsModalOpen(true);
                    break;
            }

            if (['ArrowUp', 'ArrowDown', 'T', 't', 'S', 's'].includes(event.key)) {
                event.preventDefault();
                event.stopPropagation();
            }
        }, 100);

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigateToPreviousConversation, navigateToNextConversation]);


    return (
        <div className={clsx(styles.root, isDrawerOpen && styles.drawerOpen)}>
            <FlexColumn flex={1} rowGap={0} className={styles.messagesArea}>
                <Header
                    handleShowLeadDetails={setIsShowLeadDetailsModal}
                    sendMessage={sendMessage}
                    openTourModal={() => setIsTourModalOpen(true)}
                />
                {isShowLeadDetailsModal && (
                    <SmartFieldsModal
                        open={isShowLeadDetailsModal}
                        onClose={() => setIsShowLeadDetailsModal(false)}
                        lead={lead}
                        property={property}
                        showings={showings}
                    />)}

                <MessagesContainer />
                {showCannotReplyWarning && (
                    <CannotReplyNotice
                        isAssigningLead={isAssigningLead}
                        onAssignToSelf={assignLeadToSelf}
                    />
                )}
                {isOwnerAwayTeammate && <OwnerAwayNotice />}
                <FlexColumn rowGap={0}>
                    <SentryErrorBoundary>
                        {!showCannotReplyWarning && <SmartNotifications openTourModal={() => setIsTourModalOpen(true)} />}
                        <Input />
                    </SentryErrorBoundary>
                    <Hotkeys />
                </FlexColumn>
            </FlexColumn>

            <div className={styles.sidebarWrapper}>
                <LeadSidebar
                    lead={lead}
                    property={property}
                    showings={showings}
                    messages={messages}
                    setReplyInput={setReplyInput}
                    attachments={attachments}
                    stages={stages}
                    interests={interests}
                    teamMembers={teamMembers}
                />
            </div>

            <SnippetsModal
                snippets={snippets}
                open={isSnippetsModalOpen}
                onClose={setIsSnippetsModalOpen}
                setIsAddingSnippet={setIsAddingSnippet}
                setSnippetsModalOpen={setIsSnippetsModalOpen}
            />

            {isAddingSnippet &&
                <CreateOrUpdateSnippetModal onClose={() => setIsAddingSnippet(false)} />
            }
            {isTourModalOpen && (
                <CreateOrUpdateTourModal
                    open
                    onClose={handleCloseShowingModal}
                    selectedLeadUuid={lead.uuid}
                    landlordUserId={loggedInUser.id}
                    leads={[lead]}
                    initPropertyId={lead.property_id}
                    onCreated={handleCloseShowingModal}
                />)}
        </div>
    );
}

export default function LeadMessagesPage() {
    return (
        <ThemeProvider>
            <Provider>
                <PageTitle title="Reffie | Messages">
                    <JustAddedFactsProvider>
                        <LeadMessagesPageContent />
                    </JustAddedFactsProvider>
                </PageTitle>
            </Provider>
        </ThemeProvider>
    );
}
